<template>
  <div>
    <v-container fluid class="pa-6">
      <!-- Header Section -->
      <div class="dashboard-header mb-8">
        <div class="header-content">
          <div class="header-title-section">
            <h1 class="dashboard-title">📊 สถิตินักเรียน/นักศึกษาตามระดับการศึกษา</h1>
            <p class="dashboard-subtitle">
              สำนักงานคณะกรรมการการอาชีพวศึกษา (สอศ.)
            </p>
          </div>
          <div class="header-actions">
            <v-btn
              color="success"
              variant="tonal"
              prepend-icon="mdi-file-excel"
              @click="exportToExcel"
              :loading="exporting"
              size="large"
            >
              ส่งออก Excel
            </v-btn>
            <v-btn
              color="primary"
              variant="tonal"
              prepend-icon="mdi-refresh"
              @click="fetchAllData"
              :loading="isLoadingChart"
              size="large"
            >
              รีเฟรช
            </v-btn>
          </div>
        </div>
      </div>

  
      <!-- Charts Grid - Row 1 -->
      <div class="charts-grid mb-8">
        <!-- Bar Chart: By Year -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📅 สถิติตามปีการศึกษา</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasYearData" class="chart-wrapper">
                <apexchart
                  v-if="chartByYearSeries.length > 0"
                  type="bar"
                  :options="chartByYearOptions"
                  :series="chartByYearSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>

        <!-- Pie Chart: By Level -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">🎓 สถิติตามระดับการศึกษา</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasLevelData" class="chart-wrapper">
                <apexchart
                  v-if="chartByLevelSeries.length > 0"
                  type="donut"
                  :options="chartByLevelOptions"
                  :series="chartByLevelSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Charts Grid - Row 2 -->
      <div class="charts-grid mb-8">
        <!-- Line Chart: Trend by Year and Level -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📈 แนวโน้มตามปีและระดับการศึกษา</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasTrendData" class="chart-wrapper">
                <apexchart
                  v-if="chartTrendSeries.length > 0"
                  type="line"
                  :options="chartTrendOptions"
                  :series="chartTrendSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>

        <!-- Bar Chart: By Class -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📊 สถิติตามชั้นปี</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasClassData" class="chart-wrapper">
                <apexchart
                  v-if="chartByClassSeries.length > 0"
                  type="bar"
                  :options="chartByClassOptions"
                  :series="chartByClassSeries"
                  height="380"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Stacked Bar Chart: Year vs Level -->
      <v-card class="chart-card mb-8" elevation="2">
        <v-card-text class="pa-6">
          <div class="chart-header">
            <h3 class="chart-title">📊 เปรียบเทียบข้อมูลตามปีและระดับการศึกษา</h3>
          </div>
          <div v-if="!isLoadingChart" class="chart-container">
            <div v-if="hasComparisonData" class="chart-wrapper">
              <apexchart
                v-if="chartComparisonSeries.length > 0"
                type="bar"
                :options="chartComparisonOptions"
                :series="chartComparisonSeries"
                height="400"
              />
            </div>
            <div v-else class="empty-state">
              <p>ไม่มีข้อมูล</p>
            </div>
          </div>
          <div v-else class="loading-state">
            <v-progress-linear indeterminate />
          </div>
        </v-card-text>
      </v-card>

      <!-- Data Table -->
      <v-card class="table-card" elevation="2">
        <v-card-text class="pa-6">
          <div class="table-header mb-4">
            <h3 class="table-title">📋 รายละเอียดข้อมูล</h3>
            <span class="table-info">
              จำนวนระเบียน: {{ filteredData.length }}
            </span>
          </div>

          <v-data-table
            v-if="filteredData.length > 0"
            :headers="tableHeaders"
            :items="filteredData"
            :items-per-page="10"
            class="data-table"
            elevation="0"
          >
            <template v-slot:item.countstd="{ item }">
              <span class="count-badge">
                {{ formatNumber(parseInt(item.countstd || 0)) }}
              </span>
            </template>
            <template v-slot:item.level="{ item }">
              <v-chip
                :color="getLevelColor(item.rate_work_college_class)"
                text-color="white"
                size="small"
                class="font-weight-bold"
              >
                {{ getLevelLabel(item.rate_work_college_class) }}
              </v-chip>
            </template>
          </v-data-table>

          <div v-else class="empty-table-state">
            <p>ไม่มีข้อมูล</p>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "StudentOverallClassDashboard",
  data() {
    return {
      ApiKey: "HRvec2021",
      allData: [],
      filteredData: [],
      isLoadingChart: false,
      exporting: false,
      educationLevels: [
        { label: "ทั้งหมด", value: "all" },
        { label: "ปวช. (ระดับ 2)", value: 20 },
        { label: "ปวส. (ระดับ 3)", value: 30 },
        { label: "ทล.บ. (ระดับ 4)", value: 40 }
      ],
      filters: {
        level: "all",
        rate_work_college_year: null,
        rate_work_college_class: null
      },
      tableHeaders: [
        { text: "ลำดับ", value: "index", width: "60px" },
        { text: "ปีการศึกษา", value: "rate_work_college_year", width: "120px" },
        { text: "ระดับการศึกษา", value: "rate_work_college_class", width: "100px" },   
        {
          text: "จำนวนนักเรียน/นักศึกษา",
          value: "countstd",
          width: "150px",
          align: "right"
        }
      ]
    };
  },
  computed: {
    uniqueYears() {
      return [...new Set(this.allData.map(item => item.rate_work_college_year))]
        .filter(y => y)
        .sort((a, b) => b - a);
    },
    uniqueClasses() {
      return [...new Set(this.allData.map(item => item.rate_work_college_class))]
        .filter(c => c)
        .sort();
    },
    totalStudents() {
      return this.filteredData.reduce(
        (sum, item) => sum + parseInt(item.countstd || 0),
        0
      );
    },
    class20Total() {
      return this.allData
        .filter(item => item.rate_work_college_id_class === 20)
        .reduce((sum, item) => sum + parseInt(item.countstd || 0), 0);
    },
    class30Total() {
      return this.allData
        .filter(item => item.rate_work_college_id_class === 30)
        .reduce((sum, item) => sum + parseInt(item.countstd || 0), 0);
    },
    class40Total() {
      return this.allData
        .filter(item => item.rate_work_college_id_class === 40)
        .reduce((sum, item) => sum + parseInt(item.countstd || 0), 0);
    },
    groupedByYear() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const year = item.rate_work_college_year;
        if (!grouped[year]) {
          grouped[year] = {
            20: 0,
            30: 0,
            40: 0
          };
        }
        grouped[year][item.rate_work_college_id_class] += parseInt(
          item.countstd || 0
        );
      });
      return grouped;
    },
    groupedByLevel() {
      const grouped = {
        20: 0,
        30: 0,
        40: 0
      };
      this.filteredData.forEach(item => {
        grouped[item.rate_work_college_id_class] += parseInt(
          item.countstd || 0
        );
      });
      return grouped;
    },
    groupedByClass() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const cls = item.rate_work_college_class || "ไม่ระบุ";
        if (!grouped[cls]) {
          grouped[cls] = 0;
        }
        grouped[cls] += parseInt(item.countstd || 0);
      });
      return grouped;
    },
    hasYearData() {
      return Object.keys(this.groupedByYear).length > 0;
    },
    hasLevelData() {
      return Object.values(this.groupedByLevel).some(v => v > 0);
    },
    hasClassData() {
      return Object.keys(this.groupedByClass).length > 0;
    },
    hasTrendData() {
      return Object.keys(this.groupedByYear).length > 0;
    },
    hasComparisonData() {
      return Object.keys(this.groupedByYear).length > 0;
    },

    // Chart By Year
    chartByYearSeries() {
      try {
        const years = Object.keys(this.groupedByYear).sort();
        const data = years.map(year => {
          const total =
            this.groupedByYear[year][20] +
            this.groupedByYear[year][30] +
            this.groupedByYear[year][40];
          return total;
        });
        return data.length > 0
          ? [
              {
                name: "จำนวนนักเรียน/นักศึกษา",
                data: data
              }
            ]
          : [];
      } catch (error) {
        console.error("Error in chartByYearSeries:", error);
        return [];
      }
    },
    chartByYearOptions() {
      const years = Object.keys(this.groupedByYear).sort();
      return {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#3B82F6"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "60%",
            borderRadius: 4,
            dataLabels: {
              position: "top"
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -10,
          style: {
            fontSize: "12px",
            colors: ["#3B82F6"],
            fontWeight: 600
          },
          formatter: val => this.formatNumberShort(val)
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["#ffffff"]
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: {
            top: 20,
            right: 10,
            bottom: 0,
            left: 10
          }
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        xaxis: {
          categories: years,
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (คน)",
            style: {
              fontSize: "12px",
              fontWeight: 600
            }
          },
          labels: {
            formatter: val => this.formatNumberShort(val),
            style: {
              fontSize: "11px"
            }
          }
        }
      };
    },

    // Chart By Level (Donut)
    chartByLevelSeries() {
      const data = [
        this.groupedByLevel[20],
        this.groupedByLevel[30],
        this.groupedByLevel[40]
      ];
      return data.some(v => v > 0) ? data : [];
    },
    chartByLevelOptions() {
      return {
        chart: {
          type: "donut",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#F59E0B", "#10B981", "#8B5CF6"],
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                name: {
                  fontSize: "12px"
                },
                value: {
                  fontSize: "16px",
                  fontWeight: 600,
                  formatter: val => this.formatNumberShort(val)
                },
                total: {
                  show: true,
                  fontSize: "13px",
                  fontWeight: 600,
                  label: "รวม",
                  formatter: val => this.formatNumber(val.globals.seriesTotals[0])
                }
              }
            }
          }
        },
        labels: ["ปวช. (ระดับ 2)", "ปวส. (ระดับ 3)", "ทล.บ. (ระดับ 4)"],
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontFamily: "Roboto, sans-serif"
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        dataLabels: {
          enabled: true,
          formatter: val => `${val.toFixed(1)}%`,
          style: {
            fontSize: "11px",
            fontWeight: 600
          }
        }
      };
    },

    // Chart Trend (Line)
    chartTrendSeries() {
      try {
        const years = Object.keys(this.groupedByYear).sort();
        return [
          {
            name: "ปวช. (ระดับ 2)",
            data: years.map(y => this.groupedByYear[y][20])
          },
          {
            name: "ปวส. (ระดับ 3)",
            data: years.map(y => this.groupedByYear[y][30])
          },
          {
            name: "ทล.บ. (ระดับ 4)",
            data: years.map(y => this.groupedByYear[y][40])
          }
        ];
      } catch (error) {
        console.error("Error in chartTrendSeries:", error);
        return [];
      }
    },
    chartTrendOptions() {
      const years = Object.keys(this.groupedByYear).sort();
      return {
        chart: {
          type: "line",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#F59E0B", "#10B981", "#8B5CF6"],
        stroke: {
          curve: "smooth",
          width: 3
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10
          }
        },
        markers: {
          size: 5,
          colors: ["#F59E0B", "#10B981", "#8B5CF6"],
          strokeColors: "#ffffff",
          strokeWidth: 2,
          hover: {
            size: 7
          }
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        xaxis: {
          categories: years,
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (คน)",
            style: {
              fontSize: "12px",
              fontWeight: 600
            }
          },
          labels: {
            formatter: val => this.formatNumberShort(val),
            style: {
              fontSize: "11px"
            }
          }
        },
        legend: {
          position: "top",
          fontSize: "12px",
          fontFamily: "Roboto, sans-serif"
        }
      };
    },

    // Chart By Class
    chartByClassSeries() {
      try {
        const classes = Object.keys(this.groupedByClass).sort();
        const data = classes.map(c => this.groupedByClass[c]);
        return data.length > 0
          ? [
              {
                name: "จำนวนนักเรียน/นักศึกษา",
                data: data
              }
            ]
          : [];
      } catch (error) {
        console.error("Error in chartByClassSeries:", error);
        return [];
      }
    },
    chartByClassOptions() {
      const classes = Object.keys(this.groupedByClass).sort();
      return {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#06B6D4"],
        plotOptions: {
          bar: {
            horizontal: true,
            columnWidth: "70%",
            borderRadius: 4
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 10,
          style: {
            fontSize: "12px",
            colors: ["#06B6D4"],
            fontWeight: 600
          },
          formatter: val => this.formatNumberShort(val)
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["#ffffff"]
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10
          }
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        xaxis: {
          categories: classes,
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        }
      };
    },

    // Chart Comparison (Stacked Bar)
    chartComparisonSeries() {
      try {
        const years = Object.keys(this.groupedByYear).sort();
        return [
          {
            name: "ปวช. (ระดับ 2)",
            data: years.map(y => this.groupedByYear[y][20])
          },
          {
            name: "ปวส. (ระดับ 3)",
            data: years.map(y => this.groupedByYear[y][30])
          },
          {
            name: "ทล.บ. (ระดับ 4)",
            data: years.map(y => this.groupedByYear[y][40])
          }
        ];
      } catch (error) {
        console.error("Error in chartComparisonSeries:", error);
        return [];
      }
    },
    chartComparisonOptions() {
      const years = Object.keys(this.groupedByYear).sort();
      return {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          stacked: true,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#F59E0B", "#10B981", "#8B5CF6"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "70%",
            borderRadius: 4
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["#ffffff"]
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10
          }
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        xaxis: {
          categories: years,
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (คน)",
            style: {
              fontSize: "12px",
              fontWeight: 600
            }
          },
          labels: {
            formatter: val => this.formatNumberShort(val),
            style: {
              fontSize: "11px"
            }
          }
        },
        legend: {
          position: "top",
          fontSize: "12px",
          fontFamily: "Roboto, sans-serif"
        }
      };
    }
  },
  async mounted() {
    await this.fetchAllData();
  },
  methods: {
    formatNumber(num) {
      if (!num) return "0";
      return parseInt(num).toLocaleString("th-TH");
    },
    formatNumberShort(num) {
      if (!num) return "0";
      const n = parseInt(num);
      if (n >= 1000000) {
        return (n / 1000000).toFixed(1) + "M";
      } else if (n >= 1000) {
        return (n / 1000).toFixed(1) + "K";
      }
      return n.toLocaleString("th-TH");
    },
    getLevelLabel(classId) {
      const labels = {
        20: "ปวช. (ระดับ 2)",
        30: "ปวส. (ระดับ 3)",
        40: "ทล.บ. (ระดับ 4)"
      };
      return labels[classId] || "ไม่ระบุ";
    },
    getLevelColor(classId) {
      const colors = {
        20: "amber",
        30: "green",
        40: "purple"
      };
      return colors[classId] || "grey";
    },
    async fetchAllData() {
      try {
        this.isLoadingChart = true;
        this.allData = [];

        // Fetch data from all three views
        const endpoints = [
          "vw_student_overall_class_20.php",
          "vw_student_overall_class_30.php",
          "vw_student_overall_class_40.php"
        ];

        for (const endpoint of endpoints) {
          try {
            const response = await this.$http.post(endpoint, {
              ApiKey: this.ApiKey
            });

            if (response.data && response.data.status && response.data.data) {
              const classId = parseInt(endpoint.match(/\d+/)[0]);
              const data = response.data.data.map(item => ({
                ...item,
                rate_work_college_id_class: classId
              }));
              this.allData = [...this.allData, ...data];
            }
          } catch (error) {
            console.error(`Error fetching ${endpoint}:`, error);
          }
        }

        this.applyFilters();
        this.isLoadingChart = false;
      } catch (error) {
        console.error("Error fetching all data:", error);
        this.isLoadingChart = false;
      }
    },
    applyFilters() {
      this.filteredData = this.allData
        .filter(item => {
          const levelMatch =
            this.filters.level === "all" ||
            item.rate_work_college_id_class === this.filters.level;
          const yearMatch =
            !this.filters.rate_work_college_year ||
            item.rate_work_college_year ===
              this.filters.rate_work_college_year;
          const classMatch =
            !this.filters.rate_work_college_class ||
            item.rate_work_college_class ===
              this.filters.rate_work_college_class;

          return levelMatch && yearMatch && classMatch;
        })
        .map((item, index) => ({
          ...item,
          index: index + 1
        }));
    },
    resetFilters() {
      this.filters.level = "all";
      this.filters.rate_work_college_year = null;
      this.filters.rate_work_college_class = null;
      this.applyFilters();
    },
    async exportToExcel() {
      try {
        this.exporting = true;

        // Prepare detail data
        const exportData = this.filteredData.map(item => ({
          "ลำดับ": item.index,
          "ปีการศึกษา": item.rate_work_college_year,
          "ชั้นปี": item.rate_work_college_class,
          "ระดับการศึกษา": this.getLevelLabel(item.rate_work_college_id_class),
          "จำนวนนักเรียน/นักศึกษา": parseInt(item.countstd || 0)
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wscols = [
          { wch: 8 },
          { wch: 15 },
          { wch: 12 },
          { wch: 18 },
          { wch: 25 }
        ];
        ws["!cols"] = wscols;

        // Summary sheet
        const summaryData = [
          ["สรุปข้อมูลสถิตินักเรียน/นักศึกษาตามระดับการศึกษา"],
          [],
          ["รายการ", "ค่า"],
          ["รวมนักเรียน/นักศึกษา", this.totalStudents],
          ["ปวช. (ระดับ 2)", this.class20Total],
          ["ปวส. (ระดับ 3)", this.class30Total],
          ["ทล.บ. (ระดับ 4)", this.class40Total],
          [],
          ["จำนวนปีการศึกษา", this.uniqueYears.length],
          ["จำนวนชั้นปี", this.uniqueClasses.length],
          ["วันที่ส่งออก", new Date().toLocaleString("th-TH")]
        ];

        const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
        wsSummary["!cols"] = [{ wch: 30 }, { wch: 20 }];

        // Statistics by year sheet
        const years = Object.keys(this.groupedByYear).sort();
        const yearStatsData = [["ปีการศึกษา", "ปวช.", "ปวส.", "ทล.บ.", "รวม"]];
        years.forEach(year => {
          yearStatsData.push([
            year,
            this.groupedByYear[year][20],
            this.groupedByYear[year][30],
            this.groupedByYear[year][40],
            this.groupedByYear[year][20] +
              this.groupedByYear[year][30] +
              this.groupedByYear[year][40]
          ]);
        });

        const wsYearStats = XLSX.utils.aoa_to_sheet(yearStatsData);
        wsYearStats["!cols"] = [
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 }
        ];

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsSummary, "สรุป");
        XLSX.utils.book_append_sheet(wb, wsYearStats, "สถิติตามปี");
        XLSX.utils.book_append_sheet(wb, ws, "รายละเอียด");

        // Generate filename with date
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10);
        const filename = `สถิตินักเรียนตามระดับการศึกษา_${dateStr}.xlsx`;

        XLSX.writeFile(wb, filename);

        this.$notify({
          group: "foo",
          title: "สำเร็จ",
          text: "ส่งออกข้อมูล Excel สำเร็จ!",
          type: "success"
        });
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$notify({
          group: "foo",
          title: "ข้อผิดพลาด",
          text: "เกิดข้อผิดพลาดในการส่งออกข้อมูล",
          type: "error"
        });
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.student-dashboard {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
}

/* Header Section */
.dashboard-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.header-title-section {
  flex: 1;
}

.dashboard-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.dashboard-subtitle {
  font-size: 15px;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Filter Card */
.filter-card {
  background: white;
  border-radius: 12px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  align-items: flex-end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.filter-input {
  border-radius: 6px;
}

.filter-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-reset {
  border-radius: 6px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

/* Statistics Cards */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.stat-card-total {
  border-left-color: #3b82f6;
}

.stat-card-level1 {
  border-left-color: #f59e0b;
}

.stat-card-level2 {
  border-left-color: #10b981;
}

.stat-card-level3 {
  border-left-color: #8b5cf6;
}

.stat-icon {
  font-size: 40px;
  min-width: 70px;
  text-align: center;
}

.stat-info {
  flex: 1;
  padding: 8px 0;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
}

/* Charts Grid */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 24px;
}

.chart-card {
  background: white;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.chart-header {
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 16px;
  margin-bottom: 20px;
}

.chart-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.chart-container {
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-wrapper {
  width: 100%;
}

.empty-state {
  width: 100%;
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 6px;
}

.loading-state {
  width: 100%;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table Card */
.table-card {
  background: white;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.table-card:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 16px;
}

.table-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.table-info {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.data-table {
  border-radius: 6px;
}

.data-table ::v-deep .v-data-table__wrapper {
  border-radius: 6px;
}

.data-table ::v-deep thead th {
  background: linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%) !important;
  font-weight: 700 !important;
  color: #374151 !important;
  font-size: 13px !important;
}

.data-table ::v-deep tbody tr:hover {
  background: #f9fafb !important;
}

.data-table ::v-deep tbody td {
  font-size: 13px;
  color: #1f2937;
}

.count-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-block;
}

.empty-table-state {
  width: 100%;
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 6px;
}

/* Responsive */
@media (max-width: 1200px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .dashboard-title {
    font-size: 24px;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    flex-direction: column;
    width: 100%;
  }

  .btn-reset {
    width: 100%;
  }

  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-card {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .stat-icon {
    font-size: 32px;
  }

  .stat-value {
    font-size: 24px;
  }

  .charts-grid {
    grid-template-columns: 1fr;
  }

  .chart-container {
    min-height: 320px;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 20px;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .stat-card {
    flex-direction: row;
    text-align: left;
  }

  .stat-icon {
    font-size: 28px;
    min-width: 60px;
  }

  .stat-value {
    font-size: 20px;
  }

  .data-table ::v-deep {
    font-size: 12px;
  }

  .data-table ::v-deep thead th,
  .data-table ::v-deep tbody td {
    padding: 8px !important;
  }
}
</style>